import { useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Col, Row } from 'antd';

import { AdditionalRequiredActiveProduct } from 'common/components/product-add/make-product-active/AdditionalRequiredActiveProduct';

import { RequiredPropertyInput } from 'common/components/product-add/make-product-active/RequiredPropertyInput';

import * as productFullViewSelectors from 'pages/product-full-view/controllers/selectors';
import * as productFullViewActions from 'pages/product-full-view/controllers/actions';

import {
  CustomNotification,
  StyledModal,
  WithLoading,
} from 'common/components';

import {
  RequiredModuleName,
  RequiredPropertiesForm,
  RequiredPropertiesSection,
  RequiredPropertyItem,
  RequiredPropertyName,
} from 'common/components/product-add/make-product-active/RequiredPropertyElement';

import {
  useRequiredActiveProductContext,
  useSetFormValuesAndErrors,
  useUpdateUnmatchedRequirement,
} from 'common/components/product-add/make-product-active/hooks';

import { useGetRequirementsForActiveProduct } from 'hooks/entityHooks';
import { useDispatchReloadPage, useGetProductFullView } from 'hooks';

import { useQueryMarketingAssets } from 'pages/product-full-view/queries';
import { useValidateActiveProduct } from 'pages/home/ribbon/components/sections/product/components/make-product-active/hooks';

import { mappingGridProperties as groupPropertiesByModuleName } from 'pages/home/ribbon/components/sections/customize-grid/utils';

import { filterInvalidValuesFormValues } from 'common/components/product-add/make-product-active/utils';
import { getPropertiesParams } from 'utils/minimumRequirementUtils';

import { additionalRequirementFieldsLowercase } from 'common/components/product-add/make-product-active/constants';

import {
  modalBodyProductActiveHeight,
  modalMakeProductActiveWidth,
} from '../ixone-credit-card';

export const ProductsActiveValidating = ({
  errors: errorFields,
  primaryErrors,
  entityId,
  productData,
  mode,
  additionalRequirements,
  onRevalidateActiveProduct,
}) => {
  const { allActiveRequirements } = useGetRequirementsForActiveProduct();

  const { requiredForm } = useRequiredActiveProductContext();

  const additionalFields = Object.keys(additionalRequirementFieldsLowercase);

  useSetFormValuesAndErrors({
    form: requiredForm,
    productData,
    primaryErrors,
  });

  const filteredEntityRequirement = useMemo(() => {
    let result = [];

    if (
      errorFields?.length > 0 &&
      Object.keys(allActiveRequirements)?.length > 0
    ) {
      errorFields.forEach((requiredItem) => {
        const foundProperty = allActiveRequirements[`${requiredItem}`];

        foundProperty && result.push(foundProperty);
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorFields, Object.keys(allActiveRequirements)?.length]);

  const groupedRequiredProperties = useMemo(() => {
    if (filteredEntityRequirement.length === 0) return [];

    return groupPropertiesByModuleName(filteredEntityRequirement);
  }, [filteredEntityRequirement]);

  const additionalRequiredErrorFields = errorFields.filter((field) =>
    additionalFields.includes(field)
  );

  return (
    <Row style={{ width: '100%' }} gutter={[0, 6]} className='required-section'>
      <RequiredPropertiesForm form={requiredForm} style={{ width: '100%' }}>
        {groupedRequiredProperties?.length > 0 && (
          <RequiredPropertiesSection header='Required Properties'>
            {groupedRequiredProperties.map((groupItem) => {
              return (
                <Col span={24}>
                  <RequiredModuleName
                    moduleName={groupItem.moduleDisplayName}
                    key={groupItem.moduleDisplayName}
                  />
                  {groupItem.moduleProperties.map((property) => {
                    const isError = errorFields.includes(
                      property?.fieldFullPath?.toLowerCase()
                    );

                    const error = primaryErrors.find(
                      (error) =>
                        error?.fieldName?.toLowerCase() ===
                        property?.fieldFullPath?.toLowerCase()
                    );

                    return (
                      <RequiredPropertyItem
                        key={property.fullPathDisplayName}
                        wrap={false}
                      >
                        <RequiredPropertyName
                          key={property.fullPathDisplayName}
                          propertyName={property.fullPathDisplayName}
                          isError={isError}
                          isWarning={error?.isWarning}
                        />

                        {isError && (
                          <RequiredPropertyInput
                            property={property}
                            status='error'
                            entityId={entityId}
                            primaryErrors={primaryErrors}
                            mode={mode}
                          />
                        )}
                      </RequiredPropertyItem>
                    );
                  })}
                </Col>
              );
            })}
          </RequiredPropertiesSection>
        )}

        {additionalRequiredErrorFields?.length > 0 && (
          <RequiredPropertiesSection header='Additional Properties'>
            <AdditionalRequiredActiveProduct
              requiredErrorFields={additionalRequiredErrorFields}
              entityId={entityId}
              additionalRequirements={additionalRequirements}
              onRevalidateProduct={onRevalidateActiveProduct}
            />
          </RequiredPropertiesSection>
        )}
      </RequiredPropertiesForm>
    </Row>
  );
};

export const ProductsActiveValidatingModal = ({
  entityId,
  children,
  onRevalidateActiveProduct = () => {},
  onCancel,
  mode,
  ...otherProps
}) => {
  const { requiredForm } = useRequiredActiveProductContext();

  const unmatchedRequirementMutation = useUpdateUnmatchedRequirement();

  const { invalidateMarketingAssets } = useQueryMarketingAssets({
    enabled: false,
  });

  const { handleRefetchProductFullView } = useGetProductFullView({
    productId: entityId,
    enabled: false,
  });

  const { handleRevalidateActiveProduct } = useValidateActiveProduct({
    productIds: [entityId],
    enabled: false,
  });

  const reloadPage = useDispatchReloadPage();

  const dispatch = useDispatch();

  const productHierarchyInfo = useSelector(
    productFullViewSelectors.makeSelectCurrentHierarchy()
  );

  const handleUpdateUnmatchedRequirement = async () => {
    try {
      await requiredForm.validateFields();

      const formValues = filterInvalidValuesFormValues(
        requiredForm.getFieldsValue()
      );

      const requestParams = {
        entityId,
        entityName: 'PIM',
        properties: getPropertiesParams(formValues),
      };

      unmatchedRequirementMutation.mutate(requestParams, {
        onSuccess: (response) => {
          if (response?.isSuccess) {
            mode === 'add' && reloadPage();
            onRevalidateActiveProduct(formValues);
            invalidateMarketingAssets();
            handleRevalidateActiveProduct();
            handleRefetchProductFullView();
            dispatch(
              productFullViewActions.getProductImagery({
                hierarchyId: productHierarchyInfo?.productHierarchyId,
                productId: entityId,
              })
            );
            onCancel();
            CustomNotification.success(
              'Product requirements updated successfully.'
            );
          } else {
            CustomNotification.error(
              response?.error?.message ||
                'Failed to update product requirements.'
            );
          }
        },
        onError: (error) => {
          CustomNotification.error(
            error?.message || 'Failed to update product requirements.'
          );
        },
      });
    } catch (errorInfo) {
      CustomNotification.error(
        'Please fill out all required fields before submitting.'
      );
    }
  };

  const modalProps = {
    title: 'Update Required Values',
    width: modalMakeProductActiveWidth,
    bodyStyle: {
      ...modalBodyProductActiveHeight,
    },
    wrapClassName: 'make-product-active__validating-modal',
    closable: true,
    maskClosable: false,
    onOk: handleUpdateUnmatchedRequirement,
    onCancel,
    okButtonProps: {
      loading: unmatchedRequirementMutation.isLoading,
    },
    okText: 'Submit',
    ...otherProps,
  };

  return (
    <StyledModal {...modalProps}>
      <WithLoading
        loading={unmatchedRequirementMutation.isLoading}
        tip='Validating...'
      >
        {children}
      </WithLoading>
    </StyledModal>
  );
};
